import React, { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, Send, Search, RefreshCw, CheckCircle2, Bot, ChevronLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export const AdminChatPanel = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [threads, setThreads] = useState([]);
    const [status, setStatus] = useState("open");
    const [search, setSearch] = useState("");
    const [searchDebounced, setSearchDebounced] = useState("");
    const [selected, setSelected] = useState(null); // thread_id
    const [detail, setDetail] = useState(null);
    const [reply, setReply] = useState("");
    const [busy, setBusy] = useState(false);
    const [aiSuggesting, setAiSuggesting] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        const t = setTimeout(() => setSearchDebounced(search), 300);
        return () => clearTimeout(t);
    }, [search]);

    const loadThreads = useCallback(async () => {
        try {
            const params = {};
            if (status !== "all") params.status = status;
            if (searchDebounced) params.search = searchDebounced;
            const { data } = await api.get("/admin/chat/threads", { headers, params });
            setThreads(data);
        } catch {
            toast.error("Failed to load chats");
        }
        // eslint-disable-next-line
    }, [status, searchDebounced]);

    useEffect(() => { loadThreads(); }, [loadThreads]);

    const loadDetail = useCallback(async (threadId) => {
        try {
            const { data } = await api.get(`/admin/chat/threads/${threadId}`, { headers });
            setDetail(data);
            // Refresh threads in list so the unread dot disappears
            loadThreads();
        } catch {
            toast.error("Failed to load conversation");
        }
        // eslint-disable-next-line
    }, [loadThreads]);

    useEffect(() => {
        if (selected) loadDetail(selected);
        else setDetail(null);
    }, [selected, loadDetail]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [detail]);

    const sendReply = async (e) => {
        e?.preventDefault();
        if (!reply.trim() || !selected) return;
        setBusy(true);
        try {
            await api.post(`/admin/chat/threads/${selected}/messages`, { content: reply }, { headers });
            setReply("");
            await loadDetail(selected);
        } catch {
            toast.error("Reply failed");
        }
        setBusy(false);
    };

    const resolve = async () => {
        if (!selected || !window.confirm("Mark this conversation as resolved?")) return;
        try {
            await api.post(`/admin/chat/threads/${selected}/resolve`, {}, { headers });
            toast.success("Marked resolved");
            await loadThreads();
            await loadDetail(selected);
        } catch {
            toast.error("Could not resolve");
        }
    };

    const suggestAi = async () => {
        if (!selected) return;
        setAiSuggesting(true);
        try {
            const { data } = await api.post(`/admin/chat/threads/${selected}/ai-reply`, {}, { headers });
            setReply(data.suggested_reply || "");
            toast.success("AI suggestion loaded — edit then send");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "AI unavailable");
        }
        setAiSuggesting(false);
    };

    return (
        <div data-testid="admin-chat-panel">
            {!selected ? (
                <>
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                        <div className="relative flex-1 min-w-[240px]">
                            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name or email…"
                                className="w-full pl-10 pr-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                data-testid="admin-chat-search"
                            />
                        </div>
                        <button onClick={loadThreads} className="btn-neon" data-testid="admin-chat-refresh">
                            <RefreshCw size={14} /> Refresh
                        </button>
                    </div>

                    <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
                        {["open", "resolved", "all"].map((s) => (
                            <button
                                key={s}
                                onClick={() => setStatus(s)}
                                className={`shrink-0 px-3.5 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest border ${
                                    status === s ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]" : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid={`admin-chat-tab-${s}`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>

                    {threads.length === 0 ? (
                        <div className="glass rounded-2xl p-10 text-center text-[var(--text-secondary)]" data-testid="admin-chat-empty">
                            <MessageCircle size={32} className="mx-auto mb-3 text-[var(--text-muted)]" />
                            No conversations in this view.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {threads.map((t) => (
                                <button
                                    key={t.id}
                                    onClick={() => setSelected(t.id)}
                                    className="w-full text-left glass-elevated rounded-xl p-4 hover:border-[var(--gold)]/40 transition-colors"
                                    data-testid={`admin-chat-row-${t.id}`}
                                >
                                    <div className="flex items-start gap-3 flex-wrap">
                                        <div className="flex-1 min-w-[200px]">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-display font-bold text-white">
                                                    {t.customer_name || "Anonymous"}
                                                </span>
                                                {t.customer_email && (
                                                    <span className="text-xs text-[var(--text-muted)]">{t.customer_email}</span>
                                                )}
                                                {t.unread_admin && (
                                                    <span className="px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 inline-flex items-center gap-1">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> New
                                                    </span>
                                                )}
                                                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                                                    t.status === "resolved" ? "border-white/10 text-white/40" : "border-amber-500/40 bg-amber-500/10 text-amber-200"
                                                }`}>{t.status}</span>
                                            </div>
                                            <div className="text-xs text-[var(--text-secondary)] mt-1">
                                                Topic: {t.topic || "support"} · {t.last_message_at ? new Date(t.last_message_at).toLocaleString("en-AU") : "no messages yet"}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </>
            ) : !detail ? (
                <div className="text-center py-12 text-[var(--text-secondary)]">Loading…</div>
            ) : (
                <div data-testid="admin-chat-detail">
                    <div className="flex items-center gap-3 mb-4">
                        <button
                            onClick={() => setSelected(null)}
                            className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--gold)]"
                            data-testid="admin-chat-back"
                        >
                            <ChevronLeft size={14} /> Back
                        </button>
                        <div className="flex-1">
                            <div className="font-display text-lg font-bold text-white">{detail.thread.customer_name || "Anonymous"}</div>
                            <div className="text-xs text-[var(--text-muted)]">{detail.thread.customer_email || "—"} · topic: {detail.thread.topic}</div>
                        </div>
                        {detail.thread.status !== "resolved" && (
                            <button
                                onClick={resolve}
                                className="px-3 py-1.5 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                data-testid="admin-chat-resolve"
                            >
                                <CheckCircle2 size={11} /> Resolve
                            </button>
                        )}
                    </div>

                    <div
                        ref={scrollRef}
                        className="glass-elevated rounded-2xl p-4 h-[360px] overflow-y-auto space-y-2 mb-3"
                        data-testid="admin-chat-thread-msgs"
                    >
                        {detail.messages.length === 0 ? (
                            <div className="text-center text-xs text-[var(--text-muted)] py-12">No messages.</div>
                        ) : (
                            detail.messages.map((m) => (
                                <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                                    <div
                                        className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                                            m.sender === "admin" ? "bg-[var(--gold)] text-black rounded-br-sm" : "bg-white/10 text-white rounded-bl-sm"
                                        }`}
                                    >
                                        {m.content}
                                        <div className={`mt-0.5 text-[9px] font-mono uppercase tracking-wider ${m.sender === "admin" ? "text-black/60" : "text-white/40"}`}>
                                            {m.sender} · {new Date(m.created_at).toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" })}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <form onSubmit={sendReply} className="flex gap-2">
                        <button
                            type="button"
                            onClick={suggestAi}
                            disabled={aiSuggesting}
                            className="shrink-0 px-3 py-2.5 rounded-full border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)]/10 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-40"
                            data-testid="admin-chat-ai-suggest"
                            title="AI suggestion (review before sending)"
                        >
                            <Sparkles size={12} /> {aiSuggesting ? "Thinking…" : "AI"}
                        </button>
                        <input
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            placeholder="Type your reply…"
                            className="flex-1 px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                            data-testid="admin-chat-reply-input"
                        />
                        <button
                            type="submit"
                            disabled={busy || !reply.trim()}
                            className="px-4 py-2.5 rounded-full bg-[var(--gold)] text-black text-xs font-mono uppercase tracking-widest font-bold disabled:opacity-40 hover:bg-[#F5C932] inline-flex items-center gap-1.5"
                            data-testid="admin-chat-reply-send"
                        >
                            <Send size={12} /> Send
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};
